import { zodResolver } from '@hookform/resolvers/zod';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import defaultAvatar from '@/assets/images/profile-avatar.webp';
import { UserAvatar } from '@/components/common/user-avatar';
import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextField } from '@/components/forms';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';
import { mutationErrorTone } from '@/lib/api/mutation-toast';
import { formatPhoneUS } from '@/lib/utils/format';

import { EDIT_PROFILE_COPY, PROFILE_ERROR_CODES, PROFILE_POLICY } from '../constants';
import { useUpdateProfile } from '../hooks';
import { editProfileSchema, type ProfilePanel } from '../schemas';

import styles from './edit-profile-modal.module.scss';
import { PhoneChangeFlow } from './phone-change/phone-change-flow';

/** The editable rules plus the phone: displayed only, never sent — the Phone change owns it. */
const formSchema = editProfileSchema.extend({ phone: z.string() });
type FormValues = z.infer<typeof formSchema>;

type Props = { identity: ProfilePanel['identity']; onClose: () => void };

/** Letters and spaces only, capped as typed (US-117543 §B). */
const sanitizeName = (v: string) => v.replace(/[^A-Za-z ]/g, '').slice(0, PROFILE_POLICY.NAME_MAX);

/**
 * Edit Profile (US-117543 §A–D, Figma 1790:978377). Mounted by `<TopNav>` only while open, so
 * every open is seeded afresh — from the saved identity the Profile panel loaded, since a fresh
 * login's session user carries no email or phone.
 */
export function EditProfileModal({ identity, onClose }: Props) {
  const toast = useToast();
  const update = useUpdateProfile();
  const [changingPhone, setChangingPhone] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      email: identity.email ?? '',
      phone: identity.phone ? formatPhoneUS(identity.phone) : '',
    },
  });
  const { control } = form;
  // Read both up front: RHF only computes a formState field once it has been read, and a
  // short-circuited `!isDirty || !isValid` would leave `isValid` unsubscribed until the first blur.
  const { isDirty, isValid } = form.formState;

  async function submit({ firstName, lastName, email }: FormValues) {
    try {
      await update.mutateAsync({ firstName, lastName, email: email || null });
      onClose();
    } catch (error) {
      if (error instanceof UnauthorizedError) return; // force-logout owns it
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.EMAIL_ALREADY_EXISTS) {
        form.setError('email', { message: EDIT_PROFILE_COPY.EMAIL_TAKEN });
        return;
      }
      // AC-pinned copy wins over the server's message (as in the address form).
      toast[mutationErrorTone(error)]({ title: EDIT_PROFILE_COPY.SAVE_FAILED });
    }
  }

  // Esc / ✕ / overlay / Cancel are inert while saving, so a save never lands on a closed modal.
  const dismiss = () => {
    if (!update.isPending) onClose();
  };

  return (
    <>
      <FormModal
        open
        onClose={dismiss}
        form={form}
        onSubmit={submit}
        title={EDIT_PROFILE_COPY.TITLE}
        size="lg"
        submitText={EDIT_PROFILE_COPY.SAVE}
        loading={update.isPending}
        maskClosable // AC: every dismissal discards silently — no dirty-guard
        primaryDisabled={!isDirty || !isValid}
      >
        <div className={styles.body}>
          <UserAvatar
            name={`${identity.firstName} ${identity.lastName}`}
            src={defaultAvatar}
            size={94}
          />
          <div className={styles.grid}>
            <TextField
              control={control}
              name="firstName"
              label={EDIT_PROFILE_COPY.FIRST_NAME}
              required
              sanitize={sanitizeName}
              trimOnBlur
              autoComplete="given-name"
            />
            <TextField
              control={control}
              name="lastName"
              label={EDIT_PROFILE_COPY.LAST_NAME}
              required
              sanitize={sanitizeName}
              trimOnBlur
              autoComplete="family-name"
            />
            <TextField
              control={control}
              name="email"
              label={EDIT_PROFILE_COPY.EMAIL}
              type="email"
              maxLength={PROFILE_POLICY.EMAIL_MAX}
              trimOnBlur
              autoComplete="email"
            />
            {/* Read-only, not disabled: Figma draws it white. The pencil starts the Phone change. */}
            <TextField
              control={control}
              name="phone"
              label={EDIT_PROFILE_COPY.PHONE}
              required
              readOnly
              prefix="+1"
              suffix={
                <button
                  type="button"
                  className={styles.changePhone}
                  aria-label={EDIT_PROFILE_COPY.CHANGE_PHONE}
                  disabled={!identity.phone}
                  onClick={() => setChangingPhone(true)}
                >
                  <PencilSimpleIcon size={14} aria-hidden />
                </button>
              }
            />
          </div>
        </div>
      </FormModal>
      {changingPhone && identity.phone ? (
        <PhoneChangeFlow currentPhone={identity.phone} onClose={() => setChangingPhone(false)} />
      ) : null}
    </>
  );
}
